/**
 * AdminQueueScoreBox: the tile beside the /admin brief (mockup 01's score
 * tile position). The admin portal totals queues, not a 0-100 posture score,
 * so it is a plain Carbon tile and not ScoreTile: the "Open items" title, the
 * light total, then one proportional bar per queue (WorkloadBars), scaled to
 * the busiest queue so it reads the same at 2 items or 200.
 *
 * No verdict on the tile: verdicts live on the Guardian Score tile only
 * (blueprint D22). A count that is not known yet renders a dash, never 0.
 */
import { BigNumber } from '@/components/carbon/BigNumber'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { WorkloadBars } from '@/components/carbon/WorkloadBars'

interface AdminQueueScoreBoxProps {
  /** Sum of all pending items across the three queues; null while any queue is unknown. */
  totalPending: number | null
  /** Accepted for compatibility; the verdict renders on the Guardian Score tile only (D22). */
  verdict?: string
  /** Per-queue counts for the bars; null when the queues are unknown. */
  breakdown: { orgs: number; people: number; tenants: number } | null
  /** While the queues load: a dash for the total and skeleton bars. */
  isLoading?: boolean
  className?: string
}

export default function AdminQueueScoreBox({ totalPending, breakdown, isLoading, className }: AdminQueueScoreBoxProps) {
  return (
    <Tile as="section" padding={24} aria-labelledby="admin-queue-title" aria-busy={isLoading || undefined} className={className}>
      <TileHeader title="Open items" id="admin-queue-title" />
      <BigNumber value={isLoading ? null : totalPending} size="score" className="mt-4" />
      <div className="mt-6">
        {isLoading ? (
          <Skeleton lines={3} />
        ) : breakdown ? (
          <WorkloadBars
            label="Open items by queue"
            rows={[
              { label: 'Organizations', value: breakdown.orgs },
              { label: 'People', value: breakdown.people },
              { label: 'Tenants', value: breakdown.tenants },
            ]}
          />
        ) : (
          <p className="m-0 text-helper text-text-helper">Guardian could not count every queue.</p>
        )}
      </div>
    </Tile>
  )
}
